import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { QueueIssue, STATUS_COLORS, useEntityQueues } from '../health/api';

const useStyles = makeStyles(theme => ({
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  value: { fontSize: 22, fontWeight: 600, lineHeight: 1.2 },
  label: { color: theme.palette.text.secondary, fontSize: 12 },
  issue: { fontSize: 13, marginBottom: theme.spacing(0.5) },
  ok: { color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  name: { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' },
  cell: { padding: theme.spacing(0.5, 1) },
}));

const fmt = (n: number) => n.toLocaleString('ru-RU');

const Issues = ({ issues }: { issues: QueueIssue[] }) => {
  const classes = useStyles();
  if (!issues.length) {
    return <Typography className={classes.ok}>Проблем не видно</Typography>;
  }
  return (
    <Box mb={1.5}>
      {issues.map((i, n) => (
        <Typography
          key={n}
          className={classes.issue}
          style={{ color: STATUS_COLORS[i.severity] }}
        >
          {i.severity === 'critical' ? '✕' : '▲'}{' '}
          {i.queue ? `${i.queue}: ` : ''}
          {i.message}
        </Typography>
      ))}
    </Box>
  );
};

const BULL_STATES = ['waiting', 'active', 'delayed', 'failed'];

/**
 * Очереди RabbitMQ (ресурсы-брокеры) или BullMQ (сервисы) по метрикам VictoriaMetrics. Видна только
 * у сущностей, для которых очереди настроены в kubernetesDiagnostics.queues.
 */
export const QueuesCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { value } = useEntityQueues(stringifyEntityRef(entity));
  if (!value || value.type === 'none') return null;

  const cell = classes.cell;
  let body;
  if (value.type === 'rabbitmq') {
    const t = value.totals;
    body = (
      <>
        <Box className={classes.tiles}>
          {[
            [fmt(t.ready), 'готовых сообщений'],
            [fmt(t.unacked), 'без ack'],
            [fmt(t.consumers), 'консьюмеров'],
            [fmt(t.publishedPerSec), 'публикаций/с'],
            [fmt(t.queues), 'очередей'],
          ].map(([v, l]) => (
            <Box key={l}>
              <Typography className={classes.value}>{v}</Typography>
              <Typography className={classes.label}>{l}</Typography>
            </Box>
          ))}
        </Box>
        <Issues issues={value.issues} />
        {value.topQueues.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={cell}>Очередь</TableCell>
                <TableCell className={cell} align="right">
                  Готовы
                </TableCell>
                <TableCell className={cell} align="right">
                  Без ack
                </TableCell>
                <TableCell className={cell} align="right">
                  Конс.
                </TableCell>
                <TableCell className={cell} align="right">
                  /с
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {value.topQueues.slice(0, 8).map(q => (
                <TableRow key={q.queue}>
                  <TableCell className={`${cell} ${classes.name}`}>
                    {q.queue}
                  </TableCell>
                  <TableCell className={cell} align="right">
                    {fmt(q.ready)}
                  </TableCell>
                  <TableCell className={cell} align="right">
                    {fmt(q.unacked)}
                  </TableCell>
                  <TableCell className={cell} align="right">
                    {q.consumers}
                  </TableCell>
                  <TableCell className={cell} align="right">
                    {fmt(q.publishedPerSec)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </>
    );
  } else {
    body = (
      <>
        <Issues issues={value.issues} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={cell}>Очередь</TableCell>
              {BULL_STATES.map(s => (
                <TableCell key={s} className={cell} align="right">
                  {s}
                </TableCell>
              ))}
              <TableCell className={cell} align="right">
                failed за час
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {value.queues.map(q => (
              <TableRow key={q.queue}>
                <TableCell className={`${cell} ${classes.name}`}>
                  {q.queue}
                </TableCell>
                {BULL_STATES.map(s => (
                  <TableCell key={s} className={cell} align="right">
                    {fmt(q.counts[s] ?? 0)}
                  </TableCell>
                ))}
                <TableCell className={cell} align="right">
                  {q.failedDelta1h > 0 ? `+${fmt(q.failedDelta1h)}` : '0'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>
    );
  }

  return (
    <Grid item md={6} xs={12}>
      <InfoCard
        title={
          value.type === 'rabbitmq' ? 'Очереди RabbitMQ' : 'Очереди BullMQ'
        }
      >
        {body}
      </InfoCard>
    </Grid>
  );
};
